import type { ChecklistTaskDraft } from './checklistTaskSyntax';
import { noteTitleFallback } from './noteMarkdown';
import { base64ToUtf8, utf8ToBase64 } from './textCodec';

// v1: legacy "media bytes are base64 inside ciphertext" envelopes.
type PayloadV1Text = {
  v: 1;
  type: 'text';
  text: string;
};

type PayloadV1Audio = {
  v: 1;
  type: 'audio';
  mimeType: string;
  durationMs: number;
  audioBase64: string;
};

type PayloadV1Video = {
  v: 1;
  type: 'video';
  mimeType: string;
  durationMs: number;
  videoBase64: string;
};

type PayloadV1Image = {
  v: 1;
  type: 'image';
  mimeType: string;
  imageBase64: string;
  thumbnailBase64?: string;
};

// v2: bytes are uploaded out-of-band to /api/chats/{id}/attachments and the
// ciphertext only carries metadata. This is what new clients emit; v1 stays
// supported for back-compat when an older message is fetched.
type PayloadV2Audio = {
  v: 2;
  type: 'audio';
  mimeType: string;
  durationMs: number;
  attachmentId: string;
  sizeBytes?: number;
};

type PayloadV2Video = {
  v: 2;
  type: 'video';
  mimeType: string;
  durationMs: number;
  attachmentId: string;
  sizeBytes?: number;
  thumbnailBase64?: string;
  caption?: string;
};

type PayloadV2Image = {
  v: 2;
  type: 'image';
  mimeType: string;
  attachmentId: string;
  sizeBytes?: number;
  thumbnailBase64?: string;
  /**
   * Подпись под фото (стори 13.2). Поле необязательное: клиенты, которые о нём
   * не знают, просто покажут снимок без текста — ломать совместимость ради
   * подписи незачем.
   */
  caption?: string;
  /**
   * Общий идентификатор пачки: снимки, выбранные и отправленные разом, несут
   * один и тот же `albumId` и показываются в чате одним блоком-сеткой.
   *
   * <p>Сообщения при этом остаются <b>отдельными</b>: у каждого свои вложение,
   * подпись, реакции и статус доставки. Альбом — это способ показа, а не новый
   * тип сообщения. Поэтому и поле необязательное: клиент, который о нём не
   * знает, покажет ровно то же, что показывал раньше, — снимки по одному.
   */
  albumId?: string;
};

type PayloadV2ChannelPost = {
  v: 2;
  type: 'channel_post';
  postId: string;
  title: string;
  slug: string;
  channelSlug: string;
  coverImageUrl?: string;
  previewText?: string;
};

/**
 * Карточка контакта: пользователь, которым поделились в переписке.
 * Аватар не вкладывается — получатель тянет его штатным AvatarBadge по userId
 * и avatarRevision (docs/stories/5.11-share-contact-card.md).
 */
type PayloadV2ContactCard = {
  v: 2;
  type: 'contact_card';
  userId: string;
  displayName: string;
  publicHandle?: string;
  hasAvatar?: boolean;
  avatarRevision?: number;
  avatarShape?: string;
  bio?: string;
  statusText?: string;
};

/**
 * Пункт чек-листа.
 *
 * <p>Три последних поля — задачные (заказ владельца 2026-09-03): описание, срок и исполнитель,
 * набранные прямо в строке пункта (`checklistTaskSyntax.ts`). Все необязательные, и это не
 * послабление, а совместимость: чек-листы, отправленные до них, лежат в базе как есть, а
 * клиент постарше покажет новый пункт ровно так же, как показывал раньше, — строкой с
 * галочкой. Ради того же и отдельного типа `task` здесь нет: задача — это пункт чек-листа с
 * дополнениями, а не вторая, параллельная сущность (эпик 9, стори 9.2).
 */
export type ChecklistItem = {
  id: string;
  text: string;
  checked: boolean;
  /** Кто поставил галочку — только он может снять. */
  checkedByUserId?: string;
  checkedByDisplayName?: string;
  /** Описание из круглых скобок. */
  note?: string;
  /** Срок, ISO 8601. */
  dueAt?: string;
  /** IANA-зона, в которой срок задавали. */
  timeZone?: string;
  /** Ник исполнителя без решётки, в канонической форме. */
  assigneeHandle?: string;
};

type PayloadV2Checklist = {
  v: 2;
  type: 'checklist';
  items: ChecklistItem[];
};

/** Цвет заметки — из палитры темы, а не произвольный: иначе он разъедется с тёмной темой. */
export type NoteColor = 'default' | 'amber' | 'mint' | 'lilac' | 'rose';

export const NOTE_COLORS: NoteColor[] = ['default', 'amber', 'mint', 'lilac', 'rose'];

/** Заметка органайзера (эпик 9, стори 9.1). */
type PayloadV2Note = {
  v: 2;
  type: 'note';
  title?: string;
  body: string;
  pinned?: boolean;
  color?: NoteColor;
};

/**
 * Документ любого типа (аудит удобства 2026-10-04, §3 #24): PDF, таблица, архив. Байты — во
 * вложениях, как у снимков; в сообщении только имя, размер и тип, по которым рисуется бабл.
 * Клиент, который этого типа не знает, покажет его «[message]» — ломать совместимость ради
 * файла незачем.
 */
type PayloadV2File = {
  v: 2;
  type: 'file';
  mimeType: string;
  attachmentId: string;
  fileName: string;
  sizeBytes?: number;
};

type PayloadAny =
  | PayloadV1Text
  | PayloadV1Audio
  | PayloadV1Video
  | PayloadV1Image
  | PayloadV2Audio
  | PayloadV2Video
  | PayloadV2Image
  | PayloadV2ChannelPost
  | PayloadV2ContactCard
  | PayloadV2Checklist
  // Без заметки в этом перечислении разбор её ветки не компилируется: `parsed` сужается
  // до остальных типов, и сравнение с 'note' TypeScript считает заведомо ложным.
  | PayloadV2Note
  | PayloadV2File;

export type ChatPayload =
  | { type: 'text'; text: string }
  | {
      type: 'audio';
      mimeType: string;
      durationMs: number;
      // Exactly one of these is present depending on payload version.
      audioBase64?: string;
      attachmentId?: string;
      sizeBytes?: number;
    }
  | {
      type: 'video';
      mimeType: string;
      durationMs: number;
      videoBase64?: string;
      attachmentId?: string;
      sizeBytes?: number;
      thumbnailBase64?: string;
      caption?: string;
    }
  | {
      type: 'image';
      mimeType: string;
      imageBase64?: string;
      attachmentId?: string;
      sizeBytes?: number;
      thumbnailBase64?: string;
      caption?: string;
      /** Снимки одной отправки: показываются сеткой (см. PayloadV2Image). */
      albumId?: string;
    }
  | {
      type: 'channel_post';
      postId: string;
      title: string;
      slug: string;
      channelSlug: string;
      coverImageUrl?: string;
      previewText?: string;
    }
  | {
      type: 'contact_card';
      userId: string;
      displayName: string;
      publicHandle?: string;
      hasAvatar?: boolean;
      avatarRevision?: number;
      avatarShape?: string;
      bio?: string;
      statusText?: string;
    }
  | {
      type: 'checklist';
      items: ChecklistItem[];
    }
  | {
      type: 'note';
      title?: string;
      body: string;
      pinned: boolean;
      color: NoteColor;
    }
  | {
      type: 'file';
      mimeType: string;
      attachmentId: string;
      fileName: string;
      sizeBytes?: number;
    };

/** Самое длинное имя файла в конверте: дальше оно всё равно не помещается в бабл. */
export const FILE_NAME_MAX_LEN = 200;

export function encodeFileAttachmentPayload(args: {
  mimeType: string;
  attachmentId: string;
  fileName: string;
  sizeBytes?: number;
}): string {
  const wrapped: PayloadV2File = {
    v: 2,
    type: 'file',
    mimeType: args.mimeType || 'application/octet-stream',
    attachmentId: args.attachmentId,
    fileName: (args.fileName.trim() || 'file').slice(0, FILE_NAME_MAX_LEN),
    ...(typeof args.sizeBytes === 'number' ? { sizeBytes: args.sizeBytes } : {}),
  };
  return utf8ToBase64(JSON.stringify(wrapped));
}

export function encodeTextPayload(text: string): string {
  const wrapped: PayloadV1Text = { v: 1, type: 'text', text };
  return utf8ToBase64(JSON.stringify(wrapped));
}

/** Legacy: keeps bytes inline. New clients should prefer the v2 helpers. */
export function encodeAudioPayload(args: {
  mimeType: string;
  durationMs: number;
  audioBase64: string;
}): string {
  const wrapped: PayloadV1Audio = {
    v: 1,
    type: 'audio',
    mimeType: args.mimeType,
    durationMs: Math.max(0, Math.round(args.durationMs)),
    audioBase64: args.audioBase64,
  };
  return utf8ToBase64(JSON.stringify(wrapped));
}

/** Legacy: keeps bytes inline. New clients should prefer the v2 helpers. */
export function encodeVideoPayload(args: {
  mimeType: string;
  durationMs: number;
  videoBase64: string;
}): string {
  const wrapped: PayloadV1Video = {
    v: 1,
    type: 'video',
    mimeType: args.mimeType,
    durationMs: Math.max(0, Math.round(args.durationMs)),
    videoBase64: args.videoBase64,
  };
  return utf8ToBase64(JSON.stringify(wrapped));
}

/** Legacy: keeps bytes inline. New clients should prefer the v2 helpers. */
export function encodeImagePayload(args: {
  mimeType: string;
  imageBase64: string;
  thumbnailBase64?: string;
}): string {
  const wrapped: PayloadV1Image = {
    v: 1,
    type: 'image',
    mimeType: args.mimeType,
    imageBase64: args.imageBase64,
    ...(args.thumbnailBase64 ? { thumbnailBase64: args.thumbnailBase64 } : {}),
  };
  return utf8ToBase64(JSON.stringify(wrapped));
}

/**
 * v2 audio payload: bytes live in the attachment store; the message only
 * carries the metadata needed to render the bubble and request the stream.
 */
export function encodeAudioAttachmentPayload(args: {
  mimeType: string;
  durationMs: number;
  attachmentId: string;
  sizeBytes?: number;
}): string {
  const wrapped: PayloadV2Audio = {
    v: 2,
    type: 'audio',
    mimeType: args.mimeType,
    durationMs: Math.max(0, Math.round(args.durationMs)),
    attachmentId: args.attachmentId,
    ...(typeof args.sizeBytes === 'number' ? { sizeBytes: args.sizeBytes } : {}),
  };
  return utf8ToBase64(JSON.stringify(wrapped));
}

/**
 * Максимальная длина подписи под медиа (стори 13.2). Лимит нужен не ради базы —
 * без него подпись превращается в способ отправить сколь угодно длинный текст
 * в обход ограничений композера.
 */
export const MEDIA_CAPTION_MAX_LEN = 1024;

/** Нормализует подпись: пусто и пробелы → её просто нет в полезной нагрузке. */
function normalizeMediaCaption(raw: string | null | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return undefined;
  return trimmed.slice(0, MEDIA_CAPTION_MAX_LEN);
}

export function encodeVideoAttachmentPayload(args: {
  mimeType: string;
  durationMs: number;
  attachmentId: string;
  sizeBytes?: number;
  thumbnailBase64?: string;
  caption?: string | null;
}): string {
  const caption = normalizeMediaCaption(args.caption);
  const wrapped: PayloadV2Video = {
    v: 2,
    type: 'video',
    mimeType: args.mimeType,
    durationMs: Math.max(0, Math.round(args.durationMs)),
    attachmentId: args.attachmentId,
    ...(typeof args.sizeBytes === 'number' ? { sizeBytes: args.sizeBytes } : {}),
    ...(args.thumbnailBase64 ? { thumbnailBase64: args.thumbnailBase64 } : {}),
    ...(caption ? { caption } : {}),
  };
  return utf8ToBase64(JSON.stringify(wrapped));
}

export function encodeImageAttachmentPayload(args: {
  mimeType: string;
  attachmentId: string;
  sizeBytes?: number;
  thumbnailBase64?: string;
  caption?: string | null;
  albumId?: string | null;
}): string {
  const caption = normalizeMediaCaption(args.caption);
  const albumId = args.albumId?.trim();
  const wrapped: PayloadV2Image = {
    v: 2,
    type: 'image',
    mimeType: args.mimeType,
    attachmentId: args.attachmentId,
    ...(typeof args.sizeBytes === 'number' ? { sizeBytes: args.sizeBytes } : {}),
    ...(args.thumbnailBase64 ? { thumbnailBase64: args.thumbnailBase64 } : {}),
    ...(caption ? { caption } : {}),
    ...(albumId ? { albumId } : {}),
  };
  return utf8ToBase64(JSON.stringify(wrapped));
}

/** Карточка контакта — обычное сообщение, серверная часть о ней ничего не знает. */
export function encodeContactCardPayload(args: {
  userId: string;
  displayName: string;
  publicHandle?: string | null;
  hasAvatar?: boolean | null;
  avatarRevision?: number | null;
  avatarShape?: string | null;
  bio?: string | null;
  statusText?: string | null;
}): string {
  const bio = args.bio?.trim();
  const statusText = args.statusText?.trim();
  const wrapped: PayloadV2ContactCard = {
    v: 2,
    type: 'contact_card',
    userId: args.userId,
    displayName: args.displayName,
    ...(args.publicHandle ? { publicHandle: args.publicHandle } : {}),
    ...(typeof args.hasAvatar === 'boolean' ? { hasAvatar: args.hasAvatar } : {}),
    ...(typeof args.avatarRevision === 'number' && Number.isFinite(args.avatarRevision)
      ? { avatarRevision: args.avatarRevision }
      : {}),
    ...(args.avatarShape ? { avatarShape: args.avatarShape } : {}),
    ...(bio ? { bio } : {}),
    ...(statusText ? { statusText } : {}),
  };
  return utf8ToBase64(JSON.stringify(wrapped));
}

/**
 * Ник исполнителя из чужого конверта. Правила ника здесь не перепроверяются (`publicHandle.ts`
 * сделает это в момент показа и перехода), но форма — да: в это поле попадает то, что клиент
 * подставит в подпись под пунктом, и пускать туда произвольную строку значит пускать туда
 * что угодно длиной с сообщение.
 */
const ASSIGNEE_HANDLE_SHAPE = /^[a-z0-9_]{1,32}$/;

/**
 * Задачные поля пункта из чужого конверта.
 *
 * <p>Строгость здесь односторонняя: непонятное поле <b>отбрасывается</b>, а пункт остаётся.
 * Пункт с испорченным сроком — это всё ещё пункт, который надо показать и который можно
 * отметить; терять его целиком из-за одной даты было бы хуже любого пропущенного срока.
 */
function decodeChecklistTaskFields(row: Partial<ChecklistItem>): Partial<ChecklistItem> {
  const out: Partial<ChecklistItem> = {};
  if (typeof row.note === 'string' && row.note.trim().length > 0) {
    out.note = row.note.trim().slice(0, 500);
  }
  if (typeof row.dueAt === 'string' && !Number.isNaN(new Date(row.dueAt).getTime())) {
    out.dueAt = row.dueAt;
    // Зона осмысленна только вместе со сроком: сама по себе она ничего не подписывает.
    if (typeof row.timeZone === 'string' && row.timeZone.trim().length > 0) {
      out.timeZone = row.timeZone.trim();
    }
  }
  if (typeof row.assigneeHandle === 'string') {
    const handle = row.assigneeHandle.trim().toLowerCase();
    if (ASSIGNEE_HANDLE_SHAPE.test(handle)) out.assigneeHandle = handle;
  }
  return out;
}

function newChecklistItemId(): string {
  // RN / browsers: crypto.randomUUID when available; fallback for older eng.
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Заметка (стори 9.1). Пустые заголовок и цвет по умолчанию не пишутся в конверт: он
 * хранится как есть у каждого сообщения, и лишние поля — это байты в каждой строке базы.
 */
export function encodeNotePayload(note: {
  title?: string;
  body: string;
  pinned?: boolean;
  color?: NoteColor;
}): string {
  const title = note.title?.trim() ?? "";
  const wrapped: PayloadV2Note = {
    v: 2,
    type: 'note',
    body: note.body,
    ...(title.length > 0 ? { title } : {}),
    ...(note.pinned ? { pinned: true } : {}),
    ...(note.color && note.color !== 'default' ? { color: note.color } : {}),
  };
  return utf8ToBase64(JSON.stringify(wrapped));
}

/**
 * Чек-лист из набранных пунктов.
 *
 * <p>Принимает и голые строки, и разобранные черновики (`checklistTaskSyntax.ts`): пункт без
 * описания, срока и исполнителя — это по-прежнему просто строка, и заставлять вызывающего
 * оборачивать её в объект ради трёх полей, которых у него нет, незачем.
 *
 * <p>Пустые дополнения в конверт не пишутся: он хранится у каждого сообщения как есть, и
 * `"note": null` в каждом пункте — это байты в каждой строке базы, ничего не значащие.
 */
export function encodeChecklistPayload(items: readonly (string | ChecklistTaskDraft)[]): string {
  const encoded: ChecklistItem[] = items.map((raw) => {
    const draft: ChecklistTaskDraft = typeof raw === 'string' ? { text: raw } : raw;
    return {
      id: newChecklistItemId(),
      text: draft.text,
      checked: false,
      ...(draft.note ? { note: draft.note } : {}),
      ...(draft.dueAt ? { dueAt: draft.dueAt } : {}),
      ...(draft.dueAt && draft.timeZone ? { timeZone: draft.timeZone } : {}),
      ...(draft.assigneeHandle ? { assigneeHandle: draft.assigneeHandle } : {}),
    };
  });
  const wrapped: PayloadV2Checklist = { v: 2, type: 'checklist', items: encoded };
  return utf8ToBase64(JSON.stringify(wrapped));
}

/**
 * Backward compatibility:
 * - v2: bytes referenced by attachmentId
 * - v1: bytes inline as base64
 * - legacy: plain text inside the outer base64
 */
export function decodePayload(ciphertext: string): ChatPayload {
  try {
    const decoded = base64ToUtf8(ciphertext);
    const parsed = JSON.parse(decoded) as PayloadAny;
    if (parsed && parsed.v === 1 && parsed.type === 'text' && typeof parsed.text === 'string') {
      return { type: 'text', text: parsed.text };
    }
    // v2 — attachment-by-reference
    if (
      parsed &&
      parsed.v === 2 &&
      parsed.type === 'audio' &&
      typeof parsed.mimeType === 'string' &&
      typeof parsed.durationMs === 'number' &&
      typeof parsed.attachmentId === 'string'
    ) {
      return {
        type: 'audio',
        mimeType: parsed.mimeType,
        durationMs: parsed.durationMs,
        attachmentId: parsed.attachmentId,
        ...(typeof parsed.sizeBytes === 'number' ? { sizeBytes: parsed.sizeBytes } : {}),
      };
    }
    if (
      parsed &&
      parsed.v === 2 &&
      parsed.type === 'video' &&
      typeof parsed.mimeType === 'string' &&
      typeof parsed.durationMs === 'number' &&
      typeof parsed.attachmentId === 'string'
    ) {
      const thumb = (parsed as PayloadV2Video).thumbnailBase64;
      const caption = normalizeMediaCaption((parsed as PayloadV2Video).caption);
      return {
        type: 'video',
        mimeType: parsed.mimeType,
        durationMs: parsed.durationMs,
        attachmentId: parsed.attachmentId,
        ...(typeof parsed.sizeBytes === 'number' ? { sizeBytes: parsed.sizeBytes } : {}),
        ...(typeof thumb === 'string' && thumb.length > 0 ? { thumbnailBase64: thumb } : {}),
        ...(caption ? { caption } : {}),
      };
    }
    if (
      parsed &&
      parsed.v === 2 &&
      parsed.type === 'image' &&
      typeof parsed.mimeType === 'string' &&
      typeof parsed.attachmentId === 'string'
    ) {
      const thumb = (parsed as PayloadV2Image).thumbnailBase64;
      const caption = normalizeMediaCaption((parsed as PayloadV2Image).caption);
      const albumId = (parsed as PayloadV2Image).albumId;
      return {
        type: 'image',
        mimeType: parsed.mimeType,
        attachmentId: parsed.attachmentId,
        ...(typeof parsed.sizeBytes === 'number' ? { sizeBytes: parsed.sizeBytes } : {}),
        ...(typeof thumb === 'string' && thumb.length > 0 ? { thumbnailBase64: thumb } : {}),
        ...(caption ? { caption } : {}),
        ...(typeof albumId === 'string' && albumId.length > 0 ? { albumId } : {}),
      };
    }
    if (
      parsed &&
      parsed.v === 2 &&
      parsed.type === 'channel_post' &&
      typeof (parsed as PayloadV2ChannelPost).postId === 'string' &&
      typeof (parsed as PayloadV2ChannelPost).title === 'string' &&
      typeof (parsed as PayloadV2ChannelPost).slug === 'string' &&
      typeof (parsed as PayloadV2ChannelPost).channelSlug === 'string'
    ) {
      const cp = parsed as PayloadV2ChannelPost;
      return {
        type: 'channel_post',
        postId: cp.postId,
        title: cp.title,
        slug: cp.slug,
        channelSlug: cp.channelSlug,
        ...(typeof cp.coverImageUrl === 'string' && cp.coverImageUrl.length > 0
          ? { coverImageUrl: cp.coverImageUrl }
          : {}),
        ...(typeof cp.previewText === 'string' && cp.previewText.length > 0
          ? { previewText: cp.previewText }
          : {}),
      };
    }
    if (
      parsed &&
      parsed.v === 2 &&
      parsed.type === 'contact_card' &&
      typeof (parsed as PayloadV2ContactCard).userId === 'string' &&
      typeof (parsed as PayloadV2ContactCard).displayName === 'string'
    ) {
      const cc = parsed as PayloadV2ContactCard;
      return {
        type: 'contact_card',
        userId: cc.userId,
        displayName: cc.displayName,
        ...(typeof cc.publicHandle === 'string' && cc.publicHandle.length > 0
          ? { publicHandle: cc.publicHandle }
          : {}),
        ...(typeof cc.hasAvatar === 'boolean' ? { hasAvatar: cc.hasAvatar } : {}),
        ...(typeof cc.avatarRevision === 'number' ? { avatarRevision: cc.avatarRevision } : {}),
        ...(typeof cc.avatarShape === 'string' && cc.avatarShape.length > 0
          ? { avatarShape: cc.avatarShape }
          : {}),
        ...(typeof cc.bio === 'string' && cc.bio.trim().length > 0 ? { bio: cc.bio.trim() } : {}),
        ...(typeof cc.statusText === 'string' && cc.statusText.trim().length > 0
          ? { statusText: cc.statusText.trim() }
          : {}),
      };
    }
    if (parsed && parsed.v === 2 && parsed.type === 'note' && typeof parsed.body === 'string') {
      const note = parsed as Partial<PayloadV2Note>;
      // Неизвестный цвет от клиента новее — это «обычная», а не сбой разбора: заметка
      // важнее своего оформления.
      const color =
        typeof note.color === 'string' && (NOTE_COLORS as string[]).includes(note.color)
          ? (note.color as NoteColor)
          : 'default';
      return {
        type: 'note',
        body: typeof note.body === 'string' ? note.body : '',
        ...(typeof note.title === 'string' && note.title.trim().length > 0
          ? { title: note.title.trim() }
          : {}),
        pinned: Boolean(note.pinned),
        color,
      };
    }
    if (
      parsed &&
      parsed.v === 2 &&
      parsed.type === 'file' &&
      typeof parsed.attachmentId === 'string' &&
      typeof parsed.fileName === 'string'
    ) {
      return {
        type: 'file',
        mimeType: typeof parsed.mimeType === 'string' ? parsed.mimeType : 'application/octet-stream',
        attachmentId: parsed.attachmentId,
        fileName: parsed.fileName.slice(0, FILE_NAME_MAX_LEN),
        ...(typeof parsed.sizeBytes === 'number' ? { sizeBytes: parsed.sizeBytes } : {}),
      };
    }
    if (parsed && parsed.v === 2 && parsed.type === 'checklist' && Array.isArray(parsed.items)) {
      const items: ChecklistItem[] = [];
      for (const raw of parsed.items) {
        if (!raw || typeof raw !== 'object') continue;
        const row = raw as Partial<ChecklistItem>;
        if (typeof row.id !== 'string' || typeof row.text !== 'string') continue;
        items.push({
          id: row.id,
          text: row.text,
          checked: Boolean(row.checked),
          ...(typeof row.checkedByUserId === 'string' && row.checkedByUserId.length > 0
            ? { checkedByUserId: row.checkedByUserId }
            : {}),
          ...(typeof row.checkedByDisplayName === 'string' && row.checkedByDisplayName.length > 0
            ? { checkedByDisplayName: row.checkedByDisplayName }
            : {}),
          ...decodeChecklistTaskFields(row),
        });
      }
      if (items.length >= 1) {
        return { type: 'checklist', items };
      }
    }
    // v1 — bytes inline as base64
    if (
      parsed &&
      parsed.v === 1 &&
      parsed.type === 'audio' &&
      typeof parsed.mimeType === 'string' &&
      typeof parsed.durationMs === 'number' &&
      typeof parsed.audioBase64 === 'string'
    ) {
      return {
        type: 'audio',
        mimeType: parsed.mimeType,
        durationMs: parsed.durationMs,
        audioBase64: parsed.audioBase64,
      };
    }
    if (
      parsed &&
      parsed.v === 1 &&
      parsed.type === 'video' &&
      typeof parsed.mimeType === 'string' &&
      typeof parsed.durationMs === 'number' &&
      typeof parsed.videoBase64 === 'string'
    ) {
      return {
        type: 'video',
        mimeType: parsed.mimeType,
        durationMs: parsed.durationMs,
        videoBase64: parsed.videoBase64,
      };
    }
    if (
      parsed &&
      parsed.v === 1 &&
      parsed.type === 'image' &&
      typeof parsed.mimeType === 'string' &&
      typeof parsed.imageBase64 === 'string'
    ) {
      const thumb = (parsed as PayloadV1Image).thumbnailBase64;
      return {
        type: 'image',
        mimeType: parsed.mimeType,
        imageBase64: parsed.imageBase64,
        ...(typeof thumb === 'string' && thumb.length > 0 ? { thumbnailBase64: thumb } : {}),
      };
    }
    return { type: 'text', text: decoded };
  } catch {
    return { type: 'text', text: ciphertext };
  }
}

/** Short line for notifications / chat list (matches server `previewFromCiphertext` semantics). */
export function previewTextFromCiphertext(ciphertext: string): string {
  try {
    const p = decodePayload(ciphertext);
    if (p.type === 'text') return p.text;
    if (p.type === 'channel_post') return p.title.trim() || '[article]';
    if (p.type === 'contact_card') return p.displayName.trim() || '[contact]';
    if (p.type === 'checklist') {
      const done = p.items.filter((i) => i.checked).length;
      return `☐ ${done}/${p.items.length}`;
    }
    /**
     * Заметка (стори 9.1): заголовок, а если своего нет — первая строка тела без разметки.
     * Иначе в списке чатов и в цитате стояло бы «[message]» — по нему нельзя отличить одну
     * заметку от другой, а именно там их и ищут глазами. Сырое тело сюда не годится: `**` и
     * `[…](…)` в одну строку превью читаются как мусор.
     */
    if (p.type === 'note') return p.title?.trim() || noteTitleFallback(p.body) || '[note]';
    // Подпись информативнее «[image]»: в списке чатов и в пуше видно, о чём фото.
    if (p.type === 'image') return p.caption?.trim() || '[image]';
    if (p.type === 'video') return p.caption?.trim() || '[video]';
    if (p.type === 'audio') return '[audio]';
    if (p.type === 'file') return p.fileName ? `📎 ${p.fileName}` : '[file]';
    return '[message]';
  } catch {
    return '';
  }
}
